import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
	faCalendarWeek,
	faCarrot,
	faSeedling,
	faUtensils,
} from '@fortawesome/free-solid-svg-icons';
import {
	IconDefinition,
	faBasketShopping,
	faBurgerSoda,
	faCamera,
	faCartShopping,
	faDollarSign,
	faEye,
	faFire,
	faHandsHolding,
	faHandsHoldingHeart,
	faKitchenSet,
	faList,
	faMicrochipAi,
	faMoneyBill,
	faPlantWilt,
	faShoppingCart,
	faTimer,
	faTriangleExclamation,
	faWarehouse,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	BarElement,
	CategoryScale,
	ChartData,
	Chart as ChartJS,
	ChartOptions,
	Legend,
	LinearScale,
	Title,
	Tooltip,
} from 'chart.js';
import { useShoppingCartsContext } from 'context/ShoppingCartsContext';
import { useState } from 'react';
import { Carousel } from 'react-bootstrap';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import CountUp from 'react-countup';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { tss } from 'tss-react';

import { ModificationList } from 'api/analytics/GetModificationsV1';
import { Statistic } from 'api/analytics/GetStatisticsV1';
import Recipe from 'classes/Recipe/Search/Recipe';
import RecipeList from 'classes/Recipe/Search/RecipeList';
import ButtonCard from 'components/desktop/Dashboard/ButtonCard/ButtonCard';
import ChartDoughnut, {
	DoughnutData,
} from 'components/desktop/Dashboard/ChartDoughnut/ChartDoughnut';
import 'components/desktop/Dashboard/DashboardComponent.scss';
import LastRecipes from 'components/desktop/Dashboard/LastRecipes/LastRecipes';
import ChartBar from 'components/desktop/Dashboard/ModifiedResourcesChartBar/ModifiedResourcesChartBar';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import ENTITLEMENTS from 'enums/entitlements';
import PERMISSIONS from 'enums/permissions';
import { getPicturePath } from 'functions/getPicturePath';
import { getNumberString, getPriceString } from 'functions/numberToString';
import { arePermissionsInUserPermissions } from 'functions/tokenFunctions';
import { getValueScopeLabel } from 'functions/valueScopeConversion';
import useTranslation from 'hooks/useTranslation';
import { RootState } from 'reducers/rootReducer';
import EntityType from 'types/ShoppingCart/EntityType';
import { ShoppingCart } from 'types/ShoppingCart/ShoppingCart';
import * as UserSettings from 'types/User/UserProfile/UserSettings';

import { IconsBar } from '../Recipe/IconsBar/IconsBar';
import Receipt from '../ShoppingCart/Detail/misc/Receipt';
import ImageWithFallback2 from '../_general/Image/ImageWithFallback2';
import ImageWithFallback3 from '../_general/Image/ImageWithFallback3';
import ShoppingCartQuickInfo from '../_general/MainNavigationLeft/components/ShoppingCartQuickInfo';
import ShoppingCartQuickInfoContent from '../_general/MainNavigationLeft/components/ShoppingCartQuickInfoContent';
import NutriScoreDiagram from '../_general/NutriScoreDiagram/NutriScoreDiagram';

interface DashboardCounterProps {
	label: string;
	value: number;
}

const DashboardCounter: React.FC<DashboardCounterProps> = ({ label, value }) => {
	return (
	  <div className="col-4 mb-3" >
		<div className=" text-center h-100 border-0" style={{borderRadius: '1rem !important'}}>
		  <div className="card-body">
			<h2 className="card-title fw-bold text-primary mb-2">
			  <CountUp start={0} end={value} duration={2} separator="," />
			</h2>
			<p className="card-text text-muted" style={{fontWeight: 'bold'}}>{label}</p>
		  </div>
		</div>
	  </div>
	);
  };

interface IProps {
	isLoading: boolean;
	recipeList: RecipeList;
	recipeStatus: { isLoading: boolean; data: DoughnutData[] };
	modifications: ModificationList;
	statistics: Statistic;
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// 2) Define the shape of a data point
interface DataPoint {
	label: string;
	value: number;
}

// 3) Props for our component
interface BarChartProps {
	data: DataPoint[];
	title?: string;
}

// 4) The component
export const SimpleBarChart: React.FC<BarChartProps> = ({ data, title = 'Sample Bar Chart' }) => {
	// 5) Build the `ChartData` object
	const chartData: ChartData<'bar', number[], string> = {
		labels: data.map((d) => d.label),
		datasets: [
			{
				label: title,
				data: data.map((d) => d.value),
				backgroundColor: 'rgba(60, 134, 255, 0.6)',
				borderColor: 'rgba(60, 134, 255, 1)',
				borderWidth: 1,
			},
		],
	};

	const options: ChartOptions<'bar'> = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'top',
				display: false,
			},
			title: {
				display: !!title,
				text: title,
			},
		},
		scales: {
			x: {
				title: {
					display: false,
					text: 'Category',
				},
			},
			y: {
				beginAtZero: true,
				title: {
					display: false,
					text: 'Value',
				},
			},
		},
	};

	return <Bar data={chartData} options={options} />;
};

const DashboardComponent = (props: IProps) => {
	const { tl, t, cc } = useTranslation();

	const [hovered, setHovered] = useState(false);

	const sampleData = [
		{ label: 'Jan', value: 120 },
		{ label: 'Feb', value: 200 },
		{ label: 'Mar', value: 150 },
		{ label: 'Apr', value: 220 },
		{ label: 'May', value: 170 },
		{ label: 'Jun', value: 280 },
	];

	const customizedButtonDrawer = (label: string, icon: IconDefinition, basic?: boolean) => (
		<button
			className="btn btn-outline-primary"
			style={{
				height: '100%',
				width: '100%',
				border: '3px solid#D0C8FF',
				paddingTop: basic ? '' : 0,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				borderRadius: '0.5rem',
			}}
		>
			{!basic ? (
				<>
					<div
						style={{
							width: '2rem',
							height: '0.3rem',
							backgroundColor: '#D0C8FF',
						}}
					></div>
					<div
						style={{
							height: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '0.6rem',
						}}
					>
						<FontAwesomeIcon icon={icon}></FontAwesomeIcon> <span>{label}</span>
					</div>
				</>
			) : (
				<div
					style={{
						height: '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '0.6rem',
					}}
				>
					<FontAwesomeIcon icon={icon}></FontAwesomeIcon> <span>{label}</span>
				</div>
			)}
		</button>
	);

	const [activeIndex, setActiveIndex] = useState(0);

	const getPicture = (recipe: Recipe): JSX.Element => {
		if (recipe.image !== null) {
			return (
				<div style={{ height: '100%', overflow: 'hidden' }}>
					<img
						alt={recipe.title ? recipe.title : ''}
						src={getPicturePath(recipe.image, 'max')}
						style={{
							maxHeight: '19vh',
							width: '100%',
							objectFit: 'cover',
							objectPosition: 'center',
							display: 'block',
							borderRadius: '1rem',
						}}
					/>
				</div>
			);
		}
		return (
			<div className="dashboard-recipe-picture-placeholder d-flex justify-content-center align-items-center">
				<FontAwesomeIcon size="2x" icon={faCamera} />
			</div>
		);
	};

	const recipesCarousel = (
		<div
			className="row"
			style={{
				width: '100%',
				display: 'flex',
				alignItems: 'center',
			}}
		>
			<div className="col-2" style={{ padding: 0, paddingLeft: '2vh' }}>
				<div
					style={{
						boxSizing: 'border-box',
						whiteSpace: 'nowrap',
						display: 'flex',
						flexDirection: 'column',
						width: '100%',
					}}
				>
					<div
						style={{
							paddingTop: '1rem',
							paddingBottom: '1rem',
							borderRadius: '1rem 0 0 1rem',
							color: '#6066C1',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
						}}
					>
						<div
							style={{
								fontSize: '1em',
								fontWeight: 'bold',
								whiteSpace: 'normal', // allow text to wrap
								wordBreak: 'break-word',
								fontFamily: '',
							}}
						>
							Seasonal Recipes
						</div>
					</div>
				</div>
			</div>
			<div className="col-10" style={{ padding: 0, paddingLeft: '2vh' }}>
				<Carousel
					controls={false}
					indicators={false}
					style={{ height: '100%', width: '100%' }}
					onSlide={(selectedIndex) => setActiveIndex(selectedIndex)}
				>
					{props.recipeList.all.slice(0, 4).map((recipe, i) => (
						<Carousel.Item key={i}>
							<fieldset
								style={{
									padding: '0',
									paddingTop: '1rem',
									paddingBottom: '1rem',
									boxShadow: 'none',
									borderRadius: '2rem 2rem 2rem 2rem',
									backgroundColor: 'transparent',
									width: '100%',
									cursor: 'pointer',
								}}
								onClick={() => {
									navigate('/recipe/detail/' + recipe.id);
								}}
							>
								<div className="d-flex align-items-center">
									<div
										style={{
											position: 'relative',
											overflow: 'hidden',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											borderRadius: '2rem',
										}}
									>
										<ImageWithFallback3
											src={getPicturePath(recipe.image, 230)}
											alt="thumbnail"
											style={{
												width: 'auto',
												height: '9vh',
												objectFit: 'cover',
												objectPosition: 'center',
												display: 'block',
											}}
											placeholderStyle={{
												backgroundColor: 'grey',
												width: '100%',
												maxWidth: '12rem',
												aspectRatio: '1/1',
												height: 'auto',
												borderRadius: '2rem', // match border radius for smooth transition
											}}
											iconSize="4x"
										/>
									</div>
									<div style={{ width: '80%' }}>
										<div>
											<div
												style={{
													width: '100%',
													boxSizing: 'border-box',
													whiteSpace: 'nowrap',
													display: 'flex',
													flexDirection: 'column',
													paddingLeft: '1rem',
												}}
											>
												<div
													style={{
														marginLeft: '-2rem',
														paddingLeft: '3rem',
														paddingRight: '2rem',
														paddingTop: '1rem',
														paddingBottom: '1rem',
														borderRadius: '0 1rem 1rem 0',
														color: 'white',
														backgroundImage:
															'linear-gradient(-45deg, #99a1fd 0%, #6066C1 100%)',
														boxShadow:
															'0 0.2rem 4px 2px rgb(0 0 0 / 8%)',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: '1rem',
													}}
												>
													<div
														style={{
															fontSize: '0.8em',
															fontWeight: 'bold',
															width: '100%',
															whiteSpace: 'normal', // allow text to wrap
															wordBreak: 'break-word',
														}}
													>
														{recipe.title}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</fieldset>
						</Carousel.Item>
					))}
				</Carousel>
			</div>
		</div>
	);
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxCurrency: string = useSelector((state: RootState) => state.currency);
	const reduxUserSetting: UserSettings.Type = useSelector(
		(state: RootState) => state.userSettings
	);
	const getValueScope = (): string => {
		return getValueScopeLabel(reduxUserSetting['Recipe.List.ValueScope'] ?? null, null, true);
	};

	const tipsButtons: { label: string; icon: IconDefinition }[] = [
		{ label: 'Health', icon: faHandsHoldingHeart },
		{ label: 'Savings', icon: faMoneyBill },
		/* { label: 'Orders', icon: faShoppingCart }, */
		{ label: 'Stock', icon: faWarehouse },
	];
	const recipesCarouselInfo = (
		<Carousel
			controls={false}
			indicators={false}
			style={{ height: '100%' }}
			onSlide={(selectedIndex) => setActiveIndex(selectedIndex)}
			activeIndex={activeIndex}
		>
			{props.recipeList.all.slice(0, 4).map((recipe, i) => (
				<Carousel.Item key={i}>
					<div
						style={{
							display: 'flex',
							height: '100%',
							justifyContent: 'space-evenly',
							alignItems: 'center',
							padding: '1rem',
							textAlign: 'center',
							fontSize: '0.8em',
							marginTop: '1rem',
						}}
					>
						<p>
							{recipe.calories && (
								<>
									<b>
										<span id="icon" style={{ marginRight: '0.2rem' }}>
											<FontAwesomeIcon icon={faUtensils as IconProp} />
										</span>

										{getNumberString(recipe.calories, reduxCultureCode, true)}
									</b>
									<br></br>
									{' ' + t('_general:CALORIES') + getValueScope()}
								</>
							)}
						</p>
						<p>
							{recipe.price && (
								<>
									<span id="icon" style={{ marginRight: '0.2rem' }}>
										<FontAwesomeIcon icon={faDollarSign as IconProp} />
									</span>
									<b>{getPriceString(recipe.price, reduxCultureCode)}</b>
									<br></br>
									{' ' + reduxCurrency + getValueScope()}
								</>
							)}
						</p>
						<p>
							{recipe.prepareTime && (
								<>
									<span id="icon" style={{ marginRight: '0.2rem' }}>
										<FontAwesomeIcon icon={faTimer as IconProp} />
									</span>
									<b>{Math.round(recipe.prepareTime.timeValue)}</b>
									<br></br>
									{' ' + recipe.prepareTime.timeUnit}
								</>
							)}
						</p>
					</div>
				</Carousel.Item>
			))}
		</Carousel>
	);

	const carts = useShoppingCartsContext();
	const navigate = useNavigate();

	const { classes, cx } = useStyles();

	const defaultCart = carts.getDefault();

	if (!defaultCart) return <></>;

	const renderContent = () => {
		return (
			<main className="container">
				<div className="container">
					<h1>{t('header:DASHBOARD')}</h1>

					<div>
						<div className="d-flex">
							<div className="container" style={{ padding: '4rem' }}>
								{/* Row 1: “— -” (8 + 4 cols) */}
								<div className="row" style={{ height: '16vh' }}>
									<div
										className="col-4"
										style={{ padding: '0.2rem', height: '60vh' }}
									>
										<div
											className="w-100"
											style={{
												height: '100%',
												border: '3px solid#D0C8FF',
												borderRadius: '1rem',
												padding: '1vh',
												/* boxShadow: '0 0.2rem 4px 4px rgb(0 0 0 / 8%)', */
											}}
										>
											<div
												style={{
													width: '100%',
													textAlign: 'center',
													marginTop: '1rem',
												}}
											>
												<FontAwesomeIcon
													icon={faMicrochipAi}
												></FontAwesomeIcon>{' '}
												Assistant
											</div>
											<iframe
												src="https://airflow.air-4you.xyz/chat/share?shared_id=fe4da0f28d5011f0bf30ce25661dc4db&from=agent&auth=JlMDRjNmQ4OGQ1NDExZjA4NzcyZmU1NG"
												style={{
													width: '100%',
													height: '95%',
												}}
											></iframe>
										</div>
									</div>
									<div
										className="col-8"
										style={{ padding: '2vh', height: '100%' }}
									>
										<div
											className="w-100"
											style={{
												width: '100%',
												height: '100%',
												backgroundColor: '#f8f7ff',
												borderRadius: '1rem',
											}}
										>
											<div
												className="w-100"
												style={{
													width: '100%',
													height: '100%',
													borderRadius: '1rem',
													padding: '0.5rem',
													display: 'flex',
													alignItems: 'center',
												}}
											>
												{recipesCarousel}
											</div>
										</div>
									</div>
								</div>

								{/* Row 2: “— -” (8 + 4 cols) */}
								<div
									className="row"
									style={{
										height: '22vh',
										display: 'flex',
										justifyContent: 'end',
									}}
								>
									<div
										className="col-6"
										style={{
											paddingRight: '0',
											height: '100%',
											paddingLeft: '2rem',
											borderRadius: '1rem',
											display: 'flex',
											alignItems: 'center'
										}}
									>
										{/* <SimpleBarChart data={sampleData}></SimpleBarChart> */}
										<div className="row g-3 justify-content-center">
											<RenderIf
												permissions={[
													PERMISSIONS.READRECIPE,
													PERMISSIONS.WRITERECIPE,
												]}
											>
												<DashboardCounter
													label={t('dashboard:NUMBER_OF_RECIPES')}
													value={props.statistics['Recipe']?.count ?? 0}
												/>
											</RenderIf>

											<RenderIf
												permissions={[
													PERMISSIONS.READINGREDIENT,
													PERMISSIONS.WRITEINGREDIENT,
												]}
											>
												<DashboardCounter
													label={t('dashboard:NUMBER_OF_INGREDIENTS')}
													value={
														props.statistics['Ingredient']?.count ?? 0
													}
												/>
											</RenderIf>

											<RenderIf
												permissions={[
													PERMISSIONS.READBASEINGREDIENT,
													PERMISSIONS.WRITEBASEINGREDIENT,
												]}
											>
												<DashboardCounter
													label={t('dashboard:NUMBER_OF_BASEINGREDIENTS')}
													value={
														props.statistics['BaseIngredient']?.count ??
														0
													}
												/>
											</RenderIf>

											<RenderIf
												entitlements={[ENTITLEMENTS.MENU]}
												permissions={[
													PERMISSIONS.READMENU,
													PERMISSIONS.WRITEMENU,
												]}
											>
												<DashboardCounter
													label={t('dashboard:NUMBER_OF_MENUS')}
													value={props.statistics['Menu']?.count ?? 0}
												/>
											</RenderIf>

											<RenderIf
												entitlements={[ENTITLEMENTS.MENU_PLAN]}
												permissions={[
													PERMISSIONS.READMENUPLAN,
													PERMISSIONS.WRITEMENUPLAN,
												]}
											>
												<DashboardCounter
													label={t('dashboard:NUMBER_OF_MENUPLANS')}
													value={
														props.statistics['MenuPlanner']?.count ?? 0
													}
												/>
											</RenderIf>
										</div>
									</div>

									<div
										className="col-2"
										style={{ padding: '1rem', paddingTop: 0, height: '100%' }}
									>
										<div
											className="w-100"
											style={{
												maxHeight: '43vh',
												boxShadow: '0 0.2rem 4px 4px rgb(0 0 0 / 8%)',
												cursor: 'pointer',
											}}
											onClick={() => {
												navigate('/shoppingCart/detail/' + defaultCart.id);
											}}
										>
											<Receipt
												data={ShoppingCart.getReceiptData(defaultCart, cc)}
												total={ShoppingCart.getTotalPrice(defaultCart, cc)}
											></Receipt>
										</div>
									</div>
								</div>

								{/* Row 3: “- - -” (4 + 4 + 4 cols) */}
								<div className="row" style={{ height: '22vh' }}>
									<div style={{ flex: 1, pointerEvents: 'none' }}></div>
									<div
										className="col-3"
										style={{ padding: '0.2rem', height: '100%' }}
									>
										<div
											className="w-100"
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												height: '100%',
											}}
										>
											{customizedButtonDrawer('Recipes', faUtensils)}
											{customizedButtonDrawer('Menus', faBurgerSoda)}
											{customizedButtonDrawer('Menuplans', faCalendarWeek)}
										</div>
									</div>
									<div
										className="col-3"
										style={{ padding: '0.2rem', height: '100%' }}
									>
										<div
											className="w-100"
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												height: '100%',
											}}
										>
											{customizedButtonDrawer('Ingredients', faCarrot)}
											{customizedButtonDrawer('Base Ing.', faSeedling)}
											{customizedButtonDrawer('Accessories', faKitchenSet)}
										</div>
									</div>
									{/* <div
										className="col-2"
										style={{ padding: '0.2rem', height: '100%' }}
									>
										<div
											className="w-100"
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												height: '100%',
											}}
										>
											<div
												className="w-100"
												style={{
													backgroundImage:
														'linear-gradient(to left top, #6a4dff, #2831b9)',
													borderRadius: '0.5rem',
													height: '43vh',
													boxShadow: '0 0.2rem 4px 4px rgb(0 0 0 / 8%)',
													display: 'flex',
													flexDirection: 'column',
													padding: '1rem 0.5rem',
												}}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														color: '#fff',
														fontWeight: 'bold',
														fontSize: '1.1rem',
													}}
												>
													<FontAwesomeIcon
														icon={faMicrochipAi}
														className="me-2"
													/>
													Tips
												</div>
												<div
													style={{
														height: '100%',
														display: 'flex',
														flexDirection: 'column',
														justifyContent: 'space-around',
														padding: '1rem',
													}}
												>
													{tipsButtons.map((button, i) => (
														<button
															key={i}
															type="button"
															className="btn btn-outline-light btn-sm d-flex flex-column align-items-center"
															style={{ gap: '0.4rem' }}
														>
															<FontAwesomeIcon icon={button.icon} />
															{button.label}
														</button>
													))}
												</div>
											</div>
										</div>
									</div> */}

									<div className="col-2" style={{ pointerEvents: 'none' }}></div>
								</div>
							</div>
						</div>
					</div>
				</div>
				{/* <div className="dashboard-rows">
					<RenderIf permissions={[PERMISSIONS.READRECIPE]}>
						<div className="row">
							<div className="col-12">
								<LastRecipes
									recipes={props.recipeList}
									isLoading={props.isLoading}
								/>
							</div>
						</div>
					</RenderIf>
					<div className="row">
						<RenderIf permissions={[PERMISSIONS.READRECIPE]}>
							<div className="col-lg-4 col-md-6">
								<ButtonCard
									title={t('_general:RECIPES')}
									icon={<FontAwesomeIcon icon={faUtensils} className="fa-2x" />}
									btn1={{
										title: t('dashboard:RECIPE_SEARCH'),
										link: '/recipe/search',
									}}
									btn2={{
										title: t('dashboard:RECIPE_CREATE'),
										link: '/recipe/new',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITERECIPE,
										]),
									}}
									btn3={{
										title: t('dashboard:RECIPE_ARCHIVE'),
										link: '/recipe/archive',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITERECIPE,
										]),
									}}
								/>
							</div>
						</RenderIf>
						<RenderIf permissions={[PERMISSIONS.READINGREDIENT]}>
							<div className="col-lg-4 col-md-6">
								<ButtonCard
									title={t('_general:INGREDIENTS')}
									icon={<FontAwesomeIcon icon={faCarrot} className="fa-2x" />}
									btn1={{
										title: t('dashboard:INGREDIENT_SEARCH'),
										link: '/ingredient/search',
									}}
									btn2={{
										title: t('dashboard:INGREDIENT_CREATE'),
										link: '/ingredient/new',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEINGREDIENT,
										]),
									}}
									btn3={{
										title: t('dashboard:INGREDIENT_ARCHIVE'),
										link: '/ingredient/archive',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEINGREDIENT,
										]),
									}}
								/>
							</div>
						</RenderIf>
						<RenderIf permissions={[PERMISSIONS.READBASEINGREDIENT]}>
							<div className="col-lg-4 col-md-6">
								<ButtonCard
									title={t('_general:BASEINGREDIENTS')}
									icon={<FontAwesomeIcon icon={faSeedling} className="fa-2x" />}
									btn1={{
										title: t('dashboard:BASEINGREDIENT_SEARCH'),
										link: '/baseingredient/search',
									}}
									btn2={{
										title: t('dashboard:BASEINGREDIENT_CREATE'),
										link: '/baseingredient/new',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEBASEINGREDIENT,
										]),
									}}
									btn3={{
										title: t('dashboard:BASEINGREDIENT_ARCHIVE'),
										link: '/baseIngredient/archive',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEBASEINGREDIENT,
										]),
									}}
								/>
							</div>
						</RenderIf>
					</div>
					<div className="row">
						<RenderIf
							entitlements={[ENTITLEMENTS.MENU]}
							permissions={[PERMISSIONS.READMENU]}
						>
							<div className="col-lg-4 col-md-6">
								<ButtonCard
									title={t('_general:MENUS')}
									icon={
										<FontAwesomeIcon
											icon={faBurgerSoda as IconProp}
											className="fa-2x"
										/>
									}
									btn1={{
										title: t('dashboard:MENU_SEARCH'),
										link: '/menu/search',
									}}
									btn2={{
										title: t('dashboard:MENU_CREATE'),
										link: '/menu/new',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEMENU,
										]),
									}}
									btn3={{
										title: t('dashboard:MENU_ARCHIVE'),
										link: '/menu/archive',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEMENU,
										]),
									}}
								/>
							</div>
						</RenderIf>
						<RenderIf
							entitlements={[ENTITLEMENTS.MENU_PLAN]}
							permissions={[PERMISSIONS.READMENUPLAN]}
						>
							<div className="col-lg-4 col-md-6">
								<ButtonCard
									title={t('_general:MENU_PLANS')}
									icon={
										<FontAwesomeIcon icon={faCalendarWeek} className="fa-2x" />
									}
									btn1={{
										title: t('dashboard:MENUPLAN_SEARCH'),
										link: '/menuPlan/search',
									}}
									btn2={{
										title: t('dashboard:MENUPLAN_CREATE'),
										link: '/menuPlan/new',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEMENUPLAN,
										]),
									}}
									btn3={{
										title: t('dashboard:MENUPLAN_ARCHIVE'),
										link: '/menuPlan/archive',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEMENUPLAN,
										]),
									}}
								/>
							</div>
						</RenderIf>
						<RenderIf permissions={[PERMISSIONS.READACCESSORY]}>
							<div className="col-lg-4 col-md-6">
								<ButtonCard
									title={t('_general:ACCESSORIES')}
									icon={
										<FontAwesomeIcon
											icon={faKitchenSet as IconProp}
											className="fa-2x"
										/>
									}
									btn1={{
										title: t('dashboard:ACCESSORY_SEARCH'),
										link: '/accessory/search',
									}}
									btn2={{
										title: t('dashboard:ACCESSORY_CREATE'),
										link: '/accessory/new',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEACCESSORY,
										]),
									}}
									btn3={{
										title: t('dashboard:ACCESSORY_ARCHIVE'),
										link: '/accessory/archive',
										disabled: !arePermissionsInUserPermissions([
											PERMISSIONS.WRITEACCESSORY,
										]),
									}}
								/>
							</div>
						</RenderIf>
					</div>
					<RenderIf permissions={[PERMISSIONS.READSTATISTICSANALYTICS]}>
						<div
							className="row justify-content-between"
							style={{
								border: '1px solid #dfdfdf',
								marginLeft: '0',
								marginRight: '0',
								color: '#696969',
							}}
						>
							<RenderIf
								permissions={[PERMISSIONS.READRECIPE, PERMISSIONS.WRITERECIPE]}
							>
								<div
									className="col-lg-2"
									style={{ textAlign: 'center', paddingTop: '10px' }}
								>
									<p>{t('dashboard:NUMBER_OF_RECIPES')}</p>
									<h2>
										{props.statistics['Recipe'] !== undefined
											? props.statistics['Recipe'].count
											: 0}
									</h2>
								</div>
							</RenderIf>
							<RenderIf
								permissions={[
									PERMISSIONS.READINGREDIENT,
									PERMISSIONS.WRITEINGREDIENT,
								]}
							>
								<div
									className="col-lg-2"
									style={{ textAlign: 'center', paddingTop: '10px' }}
								>
									<p>{t('dashboard:NUMBER_OF_INGREDIENTS')}</p>
									<h2>
										{props.statistics['Ingredient'] !== undefined
											? props.statistics['Ingredient'].count
											: 0}
									</h2>
								</div>
							</RenderIf>
							<RenderIf
								permissions={[
									PERMISSIONS.READBASEINGREDIENT,
									PERMISSIONS.WRITEBASEINGREDIENT,
								]}
							>
								<div
									className="col-lg-2"
									style={{ textAlign: 'center', paddingTop: '10px' }}
								>
									<p>{t('dashboard:NUMBER_OF_BASEINGREDIENTS')}</p>
									<h2>
										{props.statistics['BaseIngredient'] !== undefined
											? props.statistics['BaseIngredient'].count
											: 0}
									</h2>
								</div>
							</RenderIf>
							<RenderIf
								entitlements={[ENTITLEMENTS.MENU]}
								permissions={[PERMISSIONS.READMENU, PERMISSIONS.WRITEMENU]}
							>
								<div
									className="col-lg-2"
									style={{ textAlign: 'center', paddingTop: '10px' }}
								>
									<p>{t('dashboard:NUMBER_OF_MENUS')}</p>
									<h2>
										{props.statistics['Menu'] !== undefined
											? props.statistics['Menu'].count
											: 0}
									</h2>
								</div>
							</RenderIf>
							<RenderIf
								entitlements={[ENTITLEMENTS.MENU_PLAN]}
								permissions={[PERMISSIONS.READMENUPLAN, PERMISSIONS.WRITEMENUPLAN]}
							>
								<div
									className="col-lg-2"
									style={{ textAlign: 'center', paddingTop: '10px' }}
								>
									<p>{t('dashboard:NUMBER_OF_MENUPLANS')}</p>
									<h2>
										{props.statistics['MenuPlanner'] !== undefined
											? props.statistics['MenuPlanner'].count
											: 0}
									</h2>
								</div>
							</RenderIf>
						</div>
					</RenderIf>
					<div className="row">
						<RenderIf permissions={[PERMISSIONS.READRECIPESTATUSANALYTICS]}>
							<div className="col-lg-4 col-md-4">
								<Card
									title={t('dashboard:STATE_INFORMATION')}
									content={
										props.recipeStatus.isLoading ? (
											<div className="recipe-status-loading-box">
												<LoadingAnimation
													isLoading={props.recipeStatus.isLoading}
												/>
											</div>
										) : (
											<ChartDoughnut
												data={props.recipeStatus.data}
												style={{ maxHeight: '350px' }}
											/>
										)
									}
								/>
							</div>
						</RenderIf>
						<RenderIf permissions={[PERMISSIONS.READMODIFICATIONSANALYTICS]}>
							<div
								className={
									arePermissionsInUserPermissions([
										PERMISSIONS.READRECIPESTATUSANALYTICS,
									])
										? 'col-lg-8 col-md-8'
										: 'col-lg-12 col-md-12'
								}
							>
								<Card
									title={t('dashboard:MODIFIED_RESSOURCES')}
									content={<ChartBar modifications={props.modifications} />}
								/>
							</div>
						</RenderIf>
					</div>
				</div> */}
			</main>
		);
	};

	return <>{renderContent()}</>;
};

export default DashboardComponent;

export const useStyles = tss.create({
	container: {
		marginRight: '1rem',
	},
	icon: {
		marginTop: '2rem',
		marginLeft: '12px',
		fontSize: '22px',
		color: '#888FF9',
	},
	card: {
		borderRadius: '0.6rem',
		backgroundColor: '#f4f5ff',
		position: 'sticky',
		top: '0',
		fontSize: '14px',
		maxWidth: '16rem',
		cursor: 'pointer',
		boxShadow: '0.2rem 0.2rem 0.2rem rgba(0, 0, 0, .025)',
	},
	titleBox: {
		color: '#6066c1',
	},
});
